import classNames from "classnames";
import React, { PropTypes } from "react";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { SubmissionError } from "redux-form";

import { EnumValue, EnumWrapper } from "../../../../utils/enums";
import { ArchiveButton, BSModal, CheckboxButton, EditButton } from "../../../common";
import { DataTable } from "../../../common/DataTable";
import BasicUserFields from "./BasicUserFields";

import { ADMINISTRATOR, AGENT, DIRECTOR, FINANCE, FINTECH, MANAGER, PLAN_ADMINISTRATOR, EXTERNAL_ROLES, roleTypeToId, MENROL_ELIGIBLE_ROLES } from "../../../../utils/Roles";
import * as types from "../../../../utils/data/dataActionTypes";
import * as dataActions from "../../../../utils/data/dataActions";
import { ACCOUNTBUSINESSUNITS_V2, CLIENTS_DROPDOWN_GRAPHQL, USERS, USERS_GRAPHQL } from "../../../../utils/data/models";
import { LANGUAGE_CODES, ROLES } from "../../../../utils/enums/enums";
import userHasAccess from "../../../../utils/userHasAccess";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../../clients/enums/clientStatusTypes";
import { ACTIVE as MENROL_ACTIVE, INACTIVE as MENROL_INACTIVE, NONEXISTENT } from "./enum/menrolStatusTypes";

import { buildGraphQLQueryParams } from "../../../common/GraphQL/GraphQLQueryHelper";
import { getMenrolInfoForTenant } from "../../../common/MultiTenancy/MultiTenancyHelper";
import { toCamelCase } from "../../../common/form/DBEnumStringHelper";
import { NO_MFA, PHONE } from "./enum/mfaTypes";
import { adaptAssociatedClientIdToList, formatAssociatedClientsDisplay } from "./usersMultiClientHelper";
import { toast } from "react-toastify";

const MOCK_PASSWORD = new Array(17).join("x") + "X1@";
const CLIENT_USERS_PARENT = "ClientUsersPage";

class UsersDataTable extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.archiveUser = this.archiveUser.bind(this);
        this.createEmptyUserModel = this.createEmptyUserModel.bind(this);
        this.createUser = this.createUser.bind(this);
        this.getData = this.getData.bind(this);
        this.setCellClassNames = this.setCellClassNames.bind(this);
        this.toggleArchivedUsers = this.toggleArchivedUsers.bind(this);
        this.updateUser = this.updateUser.bind(this);
        this.closeSuccessPopup = this.closeSuccessPopup.bind(this);
        this.onEmailChange = this.onEmailChange.bind(this);
        this.getClientsGraphQLQuery = this.getClientsGraphQLQuery.bind(this);
        this.getUsersGraphQLQuery = this.getUsersGraphQLQuery.bind(this);
        this.getViewOnlyForClientStatusType = this.getViewOnlyForClientStatusType.bind(this);

        this.viewOnlyMEV = !userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR], true);
        this.viewOnlyActive = !userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER], true);
        this.viewOnlyTerminated = !userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR], true);

        this.state = {
            showSuccessModal: false,
            isEmailChanged: false,
            currentUserRoleId: localStorage.getItem("roleId") || "",
            userFilters: [],
            initFilters: true,
            q: "",
            isMenrolEnabled: false,
        };
    }

    componentWillMount() {
        const { parent, dataActions } = this.props;
        dataActions.clearDataList(ACCOUNTBUSINESSUNITS_V2);
        // Fetch clients list for the multi-select dropdown (uses separate Redux key to avoid overwriting active client)
        const filters = [CLIENTS_DROPDOWN_GRAPHQL.archiveFilter];
        const sort = [CLIENTS_DROPDOWN_GRAPHQL.defaultSort];
        const graphQLQuery = this.getClientsGraphQLQuery(filters, sort, 0, 0, "getClients", "");
        this.props.dataActions.getGraphQL(CLIENTS_DROPDOWN_GRAPHQL, { query: graphQLQuery }, sort);
        getMenrolInfoForTenant((result) => {
            this.setState({
                isMenrolEnabled: result.isMenrolEnabled,
            });
        });
    }

    componentWillUnmount() {
        const { parent, dataActions } = this.props;
        dataActions.clearDataList(CLIENTS_DROPDOWN_GRAPHQL);
        dataActions.clearDataList(USERS_GRAPHQL);
        dataActions.clearDataList(USERS);
    }

    getViewOnlyForClientStatusType(clientStatusType) {
        switch (clientStatusType) {
            case IMPLEMENTATION.value:
                return this.viewOnlyMEV;
            case ACTIVE.value:
                return this.viewOnlyActive;
            case INACTIVE.value:
            case TERMINATED.value:
                return true;
        }
    }

    getUsersGraphQLQuery(filter, sortBy, offset, limit, queryName, q) {
        const colsStr = `
	userId
	username
	firstName
	lastName
	emailAddress
	languageCode
	roleId
	associatedClientIds
	isArchived
	lastModifiedPasswordDate
	fullName
	ssoUserId
	phone
	mfaType
	menrolStatus
	isWaitingCredentials
	associatedClients {
		clientId
		name
		isArchived
		isAutomaticMemberIdentifier
		clientStatusType
		clientNumber
		businessAccountType
	}
	associatedBu {
		accountBusinessUnitId
	}
	serviceRepresentativeFor {
		name
	}
	accountManagerFor {
		name
	}
	`;
        return buildGraphQLQueryParams("users", filter, sortBy, offset, limit, colsStr, queryName, q);
    }

    getClientsGraphQLQuery(filter, sortBy, offset, limit, queryName, q) {
        const colsStr = `
		clientId
		name
		clientStatusType
		isAutomaticMemberIdentifier
		clientNumber
		businessAccountType
		`;
        return buildGraphQLQueryParams("clients", filter, sortBy, offset, limit, colsStr, queryName, q);
    }

    closeSuccessPopup() {
        this.setState({
            showSuccessModal: false,
        });
    }

    onEmailChange(value) {
        this.setState({
            isEmailChanged: value,
        });
    }

    archiveUser(userId, isArchived) {
        this.props.dataActions.updateDataItem(USERS, { userId, isArchived: !isArchived }).then(() => {
            this.getData(this.props.offset, this.props.limit, this.state.q, this.props.sortBy, this.state.userFilters);
        });
        this.props.dataActions.clearDataList(USERS);
    }

    createEmptyUserModel() {
        const { clientId } = this.props;
        const { currentUserRoleId } = this.state;
        const predefinedRole = clientId || currentUserRoleId == MANAGER.roleId;
        return {
            associatedClientIds: clientId ? [clientId] : [],
            emailAddress: "",
            firstName: "",
            languageCode: "en-CA",
            lastName: "",
            password: "",
            mfaType: PHONE.value,
            roleId: predefinedRole ? PLAN_ADMINISTRATOR.roleId : "",
            menrolStatus: NONEXISTENT.value,
            username: "",
            associatedBu: [],
        };
    }

    createUser(model) {
        const newModel = adaptAssociatedClientIdToList(model);
        // Disable MFA for external users
        if (roleTypeToId(EXTERNAL_ROLES).includes(newModel.roleId.toString())) {
            newModel.mfaType = NO_MFA.value;
        }
        if(!this.state.isMenrolEnabled){
            newModel.menrolStatus=NONEXISTENT.value;
        }
        else if( newModel.menrolStatus===true){
            newModel.menrolStatus = ACTIVE.value;
        }
        else{
            newModel.menrolStatus =INACTIVE.value;
        }
        delete newModel.isPasswordSet;
        delete newModel.mockPassword;
        return this.props.dataActions.createDataItem(USERS, newModel).then((data) => {
            if (data.type === types.CREATE_DATA_ITEM_SUCCESS) {
                this.setState({
                    createdUserName: `${newModel.firstName} ${newModel.lastName}`,
                    showSuccessModal: true,
                });
            }
            this.getData(
                this.props.offset,
                this.props.limit,
                this.state.q,
                this.props.sortBy,
                this.state.userFilters,
                data
            );
        });
    }

    setCellClassNames(user, customClass = false) {
        return classNames({
            archived: user.isArchived,
            [customClass]: !!customClass,
        });
    }

    toggleArchivedUsers() {
        const isArchivedFiltered =
            this.state.userFilters.length > 0 ? this.state.userFilters.includes(USERS_GRAPHQL.archiveFilter) : false;
        const filters = isArchivedFiltered
            ? this.state.userFilters.filter((filter) => filter !== USERS_GRAPHQL.archiveFilter)
            : [...this.state.userFilters, USERS_GRAPHQL.archiveFilter];
        this.setState({
            userFilters: filters,
        });
        const customOffset = 0;

        this.getData(customOffset, this.props.limit, this.state.q, this.props.sortBy, filters);
    }

    updateUser(model) {
        const isManager = userHasAccess(MANAGER, true);
        const langChange =
            model.userId === this.props.auth.user.userId && model.languageCode !== this.props.auth.user.languageCode;
        const newModel = adaptAssociatedClientIdToList(model);
        if (!newModel.isPasswordSet) {
            delete newModel.password;
        }
        // Disable MFA for external users
        if (roleTypeToId(EXTERNAL_ROLES).includes(newModel.roleId.toString())) {
            newModel.mfaType = NO_MFA.value;
        }
        if (!this.state.isMenrolEnabled) {
            newModel.menrolStatus = NONEXISTENT.value;
        } 
        else if (newModel.menrolStatus === true) {
            newModel.menrolStatus = MENROL_ACTIVE.value;
        } 
        else if (
            newModel.menrolStatus === MENROL_ACTIVE.value ||
            newModel.menrolStatus === MENROL_INACTIVE.value
        ) {
            // preserve existing valid menrol status
        } 
        else {
            newModel.menrolStatus = MENROL_INACTIVE.value;
        }
        if (isManager) {
            delete newModel.associatedClientIds;
            delete newModel.associatedClients;
        }
        delete newModel.isPasswordSet;
        delete newModel.mockPassword;
        delete newModel.accountLocked;
        delete newModel.isWaitingCredentials;
        return this.props.dataActions.updateDataItem(USERS, newModel).then((data) => {
            if (langChange && data.payload.data) {
                localStorage.setItem("languageCode", data.payload.data.languageCode);
                location.reload();
            }
            this.getData(
                this.props.offset,
                this.props.limit,
                this.state.q,
                this.props.sortBy,
                this.state.userFilters,
                data
            );
        });
    }

    getAssociatedClientsWarning(user) {
        const associatedClientList = [
            ...new Set([
                ...user.serviceRepresentativeFor.map((n) => n.name),
                ...user.accountManagerFor.map((n) => n.name),
            ]),
        ];
        if (associatedClientList.length > 0) {
            return (
                <div>
                    <FormattedMessage id="archiveButton.warningMessage.clientsAssociated" />
                    <ul>
                        {associatedClientList.map((d) => (
                            <li key={d}>{d}</li>
                        ))}
                    </ul>
                </div>
            );
        }
    }

    getCols() {
        const clientUsersOnly = this.props.parent == CLIENT_USERS_PARENT;
        const isViewOnlyUser = this.getViewOnlyForClientStatusType(this.props.clientStatusType);
        const isAdmin = userHasAccess([ADMINISTRATOR, FINTECH], true);

        let cols = [
            {
                name: <FormattedMessage id="admin.users.datatable.columns.username" />,
                sortKeys: ["users.username"],
                display: (user) => <span className={this.setCellClassNames(user)}>{user.username}</span>,
                widthPct: 12,
            },
            {
                name: <FormattedMessage id="admin.users.datatable.columns.emailAddress" />,
                key: "users.email_address",
                display: (user) => <span className={this.setCellClassNames(user)}>{user.emailAddress}</span>,
                widthPct: 17,
            },
            {
                name: <FormattedMessage id="admin.users.datatable.columns.name" />,
                key: "users.full_name",
                display: (user) => (
                    <span className={this.setCellClassNames(user)}>{`${user.firstName} ${user.lastName}`}</span>
                ),
                widthPct: 17,
            },
            {
                name: <FormattedMessage id="admin.users.datatable.columns.role" />,
                key: "users.role_id",
                display: (user) => {
                    const clientNames = formatAssociatedClientsDisplay(user.associatedClients, clientUsersOnly);
                    return (
                        <span className={this.setCellClassNames(user, "role-description")}>
                            <EnumWrapper enum={ROLES}>
                                <EnumValue enumKey={user.roleId} />
                            </EnumWrapper>
                            {clientNames}
                        </span>
                    );
                },
                widthPct: 13,
            },
            {
                name: <FormattedMessage id="admin.users.datatable.columns.language" />,
                key: "users.language_code",
                display: (user) => (
                    <span className={this.setCellClassNames(user)}>
                        <EnumWrapper enum={LANGUAGE_CODES}>
                            <EnumValue enumKey={user.languageCode} />
                        </EnumWrapper>
                    </span>
                ),
                widthPct: 7,
            },
            {
                name: <FormattedMessage id="admin.users.datatable.columns.status" />,
                display: (user) => {
                    let status = user.isArchived ? "blocked" : "active";
                    status = user.isWaitingCredentials ? "waiting" : status;
                    return (
                        <div className={this.setCellClassNames(user) + ` status-indicator status-${status}`}>
                            <FormattedMessage id={`admin.users.statuses.${status}`} />
                        </div>
                    );
                },
                widthPct: 8,
            },
            this.state.isMenrolEnabled
                ? {
                      name: <FormattedMessage id="admin.users.datatable.columns.menrolStatus" />,
                      display: (user) => {
                          return (
                              <div
                                  className={
                                      this.setCellClassNames(user) +
                                      ` status-indicator status-${user.menrolStatus.toLowerCase()}`
                                  }
                              >
                                  <FormattedMessage
                                      id={`admin.users.menrolStatuses.${user.menrolStatus.toLowerCase()}`}
                                  />
                              </div>
                          );
                      },
                      widthPct: 9,
                  }
                : {
                      name: "",
                      display: () => null,
                      widthPct: 1,
                  },
            {
                name: <FormattedMessage id="datatable.actions" />,
                display: (user) => {
                    return (
                        this.generateActionButtons(
                            user,
                            !isAdmin && isViewOnlyUser,
                            clientUsersOnly
                        )
                    );
                },
                widthPct: 17
            },
        ];

        return cols;
    }

    generateActionButtons(user, viewOnly, clientUsersOnly) {
        return (
            <ButtonToolbar key={user.userId}>
                <EditButton
                    title={
                        <FormattedMessage id={`admin.users.datatable.actions.${viewOnly ? "viewUser" : "editUser"}`} />
                    }
                    confirmationTitle={
                        <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
                            {(f) => <span className="icon warning">{f}</span>}
                        </FormattedMessage>
                    }
                    confirmationMessage={<FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />}
                    confirmationValidMessage={() => (
                        <FormattedMessage
                            id="admin.users.form.emailChangedConfirmation"
                            values={{ email: user.emailAddress }}
                        />
                    )}
                    closeButtonKey="common.cancel"
                    allowSubmission={!viewOnly}
                    requiresCloseConfirmation={true}
                    confirmOnClose={true}
                    requiresConfirmation={this.state.isEmailChanged}
                    form={BasicUserFields}
                    props={{
                        clients: this.props.clients,
                        onEmailChange: this.onEmailChange,
                        disabled: viewOnly,
                        restrictedClientUser: clientUsersOnly,
                        isMenrolEnabled: this.state.isMenrolEnabled,
                    }}
                    label={<FormattedMessage id={viewOnly ? "common.view" : "common.edit"} />}
                    data={{
                        ...user,
                        associatedClientIds: user.associatedClients ? [...user.associatedClients]
                        .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }))
                        .map(c => c.clientId)
                        .join(",")
                        : "",
                        authUser: this.props.auth.user.userId,
                        password: MOCK_PASSWORD,
                        mockPassword: MOCK_PASSWORD,
                    }}
                    editFunction={this.updateUser}
                />

                {!viewOnly && !user.isArchived && (
                    <ArchiveButton
                        isArchived={user.isArchived}
                        archivedName={user.emailAddress}
                        warningMessage={this.getAssociatedClientsWarning(user)}
                        showDefaultDanger={true}
                        archiveFunction={this.archiveUser.bind(null, user.userId, user.isArchived)}
                        label={
                            user.isArchived ? (
                                <FormattedMessage id="common.unarchive" />
                            ) : (
                                <FormattedMessage id="common.archive" />
                            )
                        }
                    />
                )}
            </ButtonToolbar>
        );
    }

    generateAddButton(canAddUser) {
        const isViewOnlyUser = userHasAccess([AGENT, FINANCE], true);
        const isAdmin = userHasAccess(ADMINISTRATOR, true);
        const clientUsersOnly = this.props.parent == CLIENT_USERS_PARENT;
        const { clientStatusType, clientIsArchived, activeClient } = this.props;

        let archivedFilter = false;

        if (this.state.userFilters.length > 0) {
            for (let i = 0; this.state.userFilters.length > i; i++) {
                if (this.state.userFilters[i].includes(USERS_GRAPHQL.archiveFilter)) {
                    archivedFilter = this.state.userFilters[i].includes(USERS_GRAPHQL.archiveFilter);
                    break;
                } else {
                    archivedFilter = false;
                }
            }
        }
        return (
            <div>
                <CheckboxButton
                    className="include-archived"
                    id="archived"
                    label={<FormattedMessage id="admin.users.datatable.actions.includeArchivedUsers" />}
                    checked={!archivedFilter}
                    onClick={this.toggleArchivedUsers}
                />
                {(!clientUsersOnly ||
                    isAdmin ||
                    (!isViewOnlyUser &&
                        !clientIsArchived &&
                        [ACTIVE.value, IMPLEMENTATION.value].includes(clientStatusType))) && canAddUser && (
                    <EditButton
                        title={<FormattedMessage id="admin.users.datatable.actions.addUser" />}
                        confirmationTitle={
                            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
                                {(f) => <span className="icon warning">{f}</span>}
                            </FormattedMessage>
                        }
                        confirmationMessage={
                            <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />
                        }
                        closeButtonKey="common.cancel"
                        requiresCloseConfirmation={true}
                        confirmOnClose={true}
                        label={<FormattedMessage id="common.add" />}
                        add={true}
                        form={BasicUserFields}
                        props={{
                            clients: this.props.clients,
                            restrictedClientUser: clientUsersOnly,
                            activeClient: activeClient,
                            isMenrolEnabled: this.state.isMenrolEnabled,
                        }}
                        data={this.createEmptyUserModel()}
                        editFunction={this.createUser}
                    />
                )}
            </div>
        );
    }

    getData(offset, limit, q, sortBy, filters, data = "") {
        const { parent, clientId } = this.props;
        if (data.type === types.CREATE_DATA_ITEM_FAILURE) {
            if (toCamelCase(data.payload.error.data.error) === "userCreationError") {
                throw new SubmissionError({
                    emailAddress: {
                        messageKey: "validation.customMessages.serverErrors.userCreationError",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "buAssociatedError") {
                throw new SubmissionError({
                    emailAddress: {
                        messageKey: "validation.customMessages.serverErrors.buAssociatedError",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "usernameAlreadyExistsError") {
                throw new SubmissionError({
                    username: {
                        messageKey: "validation.customMessages.serverErrors.duplicateUsername",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "passwordLinkError") {
                throw new SubmissionError({
                    emailAddress: {
                        messageKey: "validation.customMessages.serverErrors.passwordLinkError",
                        values: {},
                        state: "error",
                    },
                });

            } else if (toCamelCase(data.payload.error.data.error) === "inactiveOrTerminatedClientAssociationError") {
                    const errorData = (((data || {}).payload || {}).error || {}).data || {};
                    const blockedClients = (errorData.message && errorData.message.blockedClients) || [];

                    throw new SubmissionError({
                        associatedClientIds: {
                            messageKey: "inactiveOrTerminatedClientAssociationError",
                            values: {
                                clients: blockedClients.join(", "),
                            },
                            state: "error",
                        },
                    });
                } 
                else if (toCamelCase(data.payload.error.data.error) === "administratorRoleCreateError") {
                    toast(
                        <FormattedMessage
                        id="admin.users.errors.administratorRoleCreateError"
                        />,
                        {
                        type: toast.TYPE.ERROR,
                        autoClose: false,
                        }
                    );
                }
            else {
                throw new SubmissionError({
                    emailAddress: {
                        messageKey: "validation.customMessages.serverErrors.generic",
                        values: {},
                        state: "error",
                    },
                });
            }
        } else if (data.type === types.UPDATE_DATA_ITEM_FAILURE) {
            if (toCamelCase(data.payload.error.data.error) === "usernameAlreadyExistsError") {
                throw new SubmissionError({
                    username: {
                        messageKey: "validation.customMessages.serverErrors.duplicateUsername",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "unauthorizedClientError") {
                throw new SubmissionError({
                    associatedClientIds: {
                        messageKey: "validation.customMessages.serverErrors.unauthorizedClientError",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) == "ssoPasswordResetError") {
                throw new SubmissionError({
                    password: {
                        messageKey: "resetPassword.errors.ssoPasswordResetError",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "externalToInternalRoleChangeError") {
                throw new SubmissionError({
                    roleId: {
                        messageKey: "externalToInternalRoleChangeError",
                        values: {},
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "inactiveOrTerminatedClientAssociationError") {
                const errorData = (((data || {}).payload || {}).error || {}).data || {};
                const blockedClients = (errorData.message && errorData.message.blockedClients) || [];

                throw new SubmissionError({
                    associatedClientIds: {
                        messageKey: "inactiveOrTerminatedClientAssociationError",
                        values: {
                            clients: blockedClients.join(", "),
                        },
                        state: "error",
                    },
                });
            } else if (toCamelCase(data.payload.error.data.error) === "administratorRoleUpdateError") {
                    toast(
                        <FormattedMessage
                        id="admin.users.errors.administratorRoleUpdateError"
                        />,
                        {
                        type: toast.TYPE.ERROR,
                        autoClose: false,
                        }
                    );
            }else if (toCamelCase(data.payload.error.data.error)=== "invalidNewPasswordError"){
                throw new SubmissionError({
                    password : {
                        messageKey:"validation.customMessages.serverErrors.invalidNewPasswordError",
                        values:{},
                        state:"error",
                    },
                });
            } else {
                throw new SubmissionError({
                    emailAddress: {
                        messageKey: "validation.customMessages.serverErrors.generic",
                        values: {},
                        state: "error",
                    },
                });
            }
        } else {
            const clientIdFilter = `users_clients_association.client_id==${clientId}`;
            const roleIdFilter = `users.role_id==${PLAN_ADMINISTRATOR.roleId}`;
            if (parent == CLIENT_USERS_PARENT) {
                !filters.includes(clientIdFilter) && filters.push(clientIdFilter);
                !filters.includes(roleIdFilter) && filters.push(roleIdFilter);
                if (this.state.initFilters) {
                    !filters.includes(USERS_GRAPHQL.archiveFilter) && filters.push(USERS_GRAPHQL.archiveFilter);
                }
                this.setState({
                    userFilters: filters,
                    initFilters: false,
                    q: q,
                });

                if (filters.length > 0) {
                    filters = [filters.join(";")];
                }
                limit = 25;
            }

            if (this.state.initFilters && parent != CLIENT_USERS_PARENT) {
                filters = [USERS_GRAPHQL.archiveFilter];
                this.setState({
                    userFilters: filters,
                    initFilters: false,
                });
            }
            const graphQLQuery = this.getUsersGraphQLQuery(filters, sortBy, offset, limit, "getUsers", q);

            let result = this.props.dataActions.getGraphQL(USERS_GRAPHQL, { query: graphQLQuery }, sortBy);
            result.then(({ payload: { data } }) => {
                data.data.users.items.forEach((user) => {
                    user.associatedBu = user.associatedBu.map((bu) => bu.accountBusinessUnitId);
                });
            });
            return result;
        }
    }

    render() {
        const { parent, clientStatusType } = this.props;

        let canAddUser = true;

        if (parent === CLIENT_USERS_PARENT) {
            canAddUser = !this.getViewOnlyForClientStatusType(clientStatusType);
        }
        return (
            <div>
                <BSModal
                    show={this.state.showSuccessModal}
                    title={
                        <FormattedMessage id="admin.users.form.successTitle">
                            {(f) => <span className="icon warning">{f}</span>}
                        </FormattedMessage>
                    }
                    closeButtonKey="common.close"
                    onClose={this.closeSuccessPopup}
                >
                    <FormattedMessage
                        id="admin.users.form.userCreationSuccess"
                        values={{ userName: this.state.createdUserName }}
                    />
                </BSModal>

                <DataTable
                    ref="dataTable"
                    title={<FormattedMessage id="admin.users.title" />}
                    getData={this.getData}
                    cols={this.getCols()}
                    offset={this.props.offset}
                    q={this.state.q}
                    filters={this.state.userFilters}
                    data={this.props.users}
                    rowsPerPage={this.props.limit}
                    totalRows={this.props.total}
                    sortBy={this.props.sortBy}
                    addButton={this.generateAddButton(canAddUser)}
                />
            </div>
        );
    }
}

UsersDataTable.propTypes = {
    auth: PropTypes.object,
    clients: PropTypes.array,
    dataActions: PropTypes.object,
    q: PropTypes.string,
    filters: PropTypes.array,
    limit: PropTypes.number,
    offset: PropTypes.number,
    sortBy: PropTypes.array,
    total: PropTypes.number,
    users: PropTypes.array,
    clientId: PropTypes.number,
    parent: PropTypes.string,
    clientStatusType: PropTypes.string,
    clientIsArchived: PropTypes.bool,
    activeClient: PropTypes.object,
};

function mapStateToProps(state) {
    const clients = state.data.clients || {};
    const data = state.data.users || {};
    let users = data.items || null;
    const pagination = data || {};
    let limit = pagination.limit || 100;
    const totalCount = pagination.totalCount || 0;
    const activeClient = clients.active || {};
    const clientId = activeClient ? activeClient.clientId : null;
    const clientStatusType = clientId ? activeClient.clientStatusType : null;
    const clientIsArchived = clientId ? activeClient.isArchived : false;
    let sortByCopy = data.sortBy ? [...data.sortBy] : null;

    if (!sortByCopy || (sortByCopy && !sortByCopy.find((sort) => sort.includes(".")))) {
        /**
         * If sortBy does not have the correct format, we assign the default one to the variable.
         * GraphQL sort by format always has a "." . Ex: "users.full_name:asc"
         */
        sortByCopy = [USERS_GRAPHQL.defaultSort];
    }
    return {
        auth: state.auth || {},
        clients: (state.data.clientsDropdown || {}).items || [],
        limit: limit,
        q: data.q,
        filters: data.filters || [USERS_GRAPHQL.archiveFilter],
        offset: pagination.offset || 0,
        sortBy: sortByCopy,
        total: totalCount,
        users: users,
        clientId: clientId,
        clientStatusType: clientStatusType,
        clientIsArchived: clientIsArchived,
        activeClient: activeClient,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dataActions: bindActionCreators(dataActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(UsersDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/admin/users/UsersDataTable.js